import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, Trash2, Heart } from "lucide-react";
import { API } from "@/lib/api";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Lymphie from "./Lymphie";

const STARTER_PROMPTS = [
  "I feel really puffy today",
  "How can I help swelling overnight?",
  "I'm frustrated with my compression garment",
  "What are gentle exercises for my legs?",
];

export default function LymphieChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [nudge, setNudge] = useState(null);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const nextIdRef = useRef(0);
  const mkMsg = (role, content) => ({ id: `m-${Date.now()}-${nextIdRef.current++}`, role, content });

  useEffect(() => {
    api.get("/lymphie/nudge").then((r) => {
      const n = r.data?.nudge;
      if (n && !localStorage.getItem(`lc_nudge_dismissed_${n.id}`)) {
        setTimeout(() => setNudge(n), 1500);
      }
    }).catch(() => {});
  }, []);

  // Global event: any component can dispatch `open-lymphie` with an optional seeded assistant message.
  useEffect(() => {
    const handler = (e) => {
      const seed = e.detail?.seedMessage;
      if (seed) {
        setMessages((prev) => (prev.length === 0 ? [mkMsg("assistant", seed)] : prev));
        setLoaded(true);
      }
      setOpen(true);
    };
    window.addEventListener("open-lymphie", handler);
    return () => window.removeEventListener("open-lymphie", handler);
  }, []);

  useEffect(() => {
    if (open && !loaded) {
      api.get("/chat/history").then((r) => {
        const arr = (r.data || []).map((m) => mkMsg(m.role, m.content));
        setMessages(arr);
        setLoaded(true);
      }).catch(() => setLoaded(true));
    }
  }, [open, loaded]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, mkMsg("user", text), mkMsg("assistant", "")]);
    setSending(true);

    try {
      const token = localStorage.getItem("lc_token");
      const ac = new AbortController();
      abortRef.current = ac;
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text }),
        signal: ac.signal,
      });
      if (!res.ok || !res.body) throw new Error("chat failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) {
              setMessages((m) => {
                const arr = [...m];
                const last = arr[arr.length - 1];
                arr[arr.length - 1] = { ...last, content: last.content + payload.delta };
                return arr;
              });
            } else if (payload.error) {
              setMessages((m) => {
                const arr = [...m];
                const last = arr[arr.length - 1];
                arr[arr.length - 1] = { ...last, content: payload.error };
                return arr;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error("Lymphie couldn't reply — try again ♡");
        setMessages((m) => m.slice(0, -1));
      }
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const clearChat = async () => {
    if (!window.confirm("Clear conversation with Lymphie?")) return;
    try {
      await api.delete("/chat/history");
      setMessages([]);
      toast.success("Chat cleared ♡");
    } catch {
      toast.error("Couldn't clear chat");
    }
  };

  return (
    <>
      <AnimatePresence>
        {nudge && !open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="fixed bottom-24 right-6 z-40 max-w-[280px] rounded-3xl overflow-hidden border border-kawaii-border shadow-xl"
            style={{ background: "linear-gradient(135deg, #FFF9F9 0%, #FFE5E5 65%, #E2F0CB 100%)" }}
            data-testid="lymphie-nudge-bubble"
          >
            <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-kawaii-pink/30 blur-2xl" />
            <button
              onClick={() => {
                localStorage.setItem(`lc_nudge_dismissed_${nudge.id}`, "1");
                setNudge(null);
              }}
              data-testid="dismiss-nudge-btn"
              className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/70 backdrop-blur border border-white/60 flex items-center justify-center z-10"
            >
              <X className="w-3 h-3 text-kawaii-textSoft" strokeWidth={2.5} />
            </button>
            <div className="relative p-4 pr-9">
              <div className="flex items-center gap-1.5 mb-2">
                <Heart className="w-3 h-3 text-kawaii-pink" strokeWidth={2.5} fill="#FFB7B2" />
                <span className="text-[9px] uppercase tracking-[0.22em] text-kawaii-textSoft font-bold">
                  Lymphie noticed something
                </span>
              </div>
              <p className="text-sm text-kawaii-text font-body leading-relaxed">
                {nudge.message}
              </p>
              <button
                onClick={() => {
                  setMessages((prev) =>
                    prev.length === 0 ? [mkMsg("assistant", nudge.message)] : prev
                  );
                  setLoaded(true);
                  localStorage.setItem(`lc_nudge_dismissed_${nudge.id}`, "1");
                  setNudge(null);
                  setOpen(true);
                }}
                data-testid="open-from-nudge-btn"
                className="kawaii-btn mt-3 w-full py-2 rounded-full text-xs font-heading font-semibold bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white shadow-sm"
              >
                Talk to Lymphie ♡
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen(true)}
        data-testid="open-lymphie-btn"
        initial={{ scale: 0 }} animate={{ scale: 1 }}
        whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-6 z-40 w-20 h-20 rounded-full bg-white/70 backdrop-blur-md shadow-xl border border-kawaii-lavender/40 flex items-center justify-center overflow-visible"
        aria-label="Open Lymphie chat"
      >
        <Lymphie size={64} waving />
        {nudge && !open && (
          <>
            <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-white shadow-md" />
            <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-kawaii-pink animate-ping" />
          </>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <motion.aside
              onClick={(e) => e.stopPropagation()}
              initial={{ x: 500, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 500, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
              className="fixed right-0 top-0 bottom-0 w-full sm:w-[440px] bg-white border-l border-kawaii-border flex flex-col shadow-2xl"
              data-testid="lymphie-panel"
            >
              <div className="p-5 border-b border-kawaii-border flex items-center justify-between bg-gradient-to-r from-kawaii-pink/10 to-kawaii-lavender/10">
                <div className="flex items-center gap-3">
                  <Lymphie size={52} />
                  <div>
                    <div className="font-heading text-lg font-semibold text-kawaii-text leading-none">Lymphie</div>
                    <div className="text-xs text-kawaii-textSoft mt-1">Your gentle AI companion</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={clearChat}
                    data-testid="clear-chat-btn"
                    title="Clear chat"
                    className="kawaii-btn w-9 h-9 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4 text-kawaii-textSoft" strokeWidth={2.5} />
                  </button>
                  <button
                    onClick={() => setOpen(false)}
                    data-testid="close-lymphie-btn"
                    className="kawaii-btn w-9 h-9 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
                  >
                    <X className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="chat-messages">
                {messages.length === 0 && (
                  <div className="text-center py-6">
                    <div className="flex justify-center mb-3">
                      <Lymphie size={92} waving />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-kawaii-text">Hi, I'm Lymphie ♡</h3>
                    <p className="text-sm text-kawaii-textSoft mt-2 font-body max-w-xs mx-auto">
                      A soft place to vent, ask about lymphedema care, or just talk. I'm here whenever you need.
                    </p>
                    <div className="mt-6 space-y-2 text-left">
                      {STARTER_PROMPTS.map((p) => (
                        <button
                          key={p}
                          onClick={() => send(p)}
                          data-testid="starter-prompt"
                          className="kawaii-btn w-full text-left px-4 py-2.5 rounded-2xl bg-white border border-kawaii-border text-sm text-kawaii-text font-body hover:bg-kawaii-pink/10"
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`
                        max-w-[85%] px-4 py-3 rounded-2xl font-body text-sm leading-relaxed
                        ${m.role === "user"
                          ? "bg-gradient-to-br from-kawaii-pink to-kawaii-peach text-white rounded-br-sm"
                          : "bg-kawaii-border/40 text-kawaii-text rounded-bl-sm"}
                      `}
                      data-testid={`chat-msg-${m.role}`}
                    >
                      {m.content || (sending && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin" /> : "")}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 border-t border-kawaii-border bg-white">
                <form
                  onSubmit={(e) => { e.preventDefault(); send(); }}
                  className="flex items-end gap-2"
                >
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Tell Lymphie how you're feeling..."
                    rows={1}
                    data-testid="chat-input"
                    className="flex-1 px-4 py-3 rounded-2xl border border-kawaii-border bg-kawaii-bg/70 font-body text-sm text-kawaii-text resize-none focus:outline-none focus:ring-2 focus:ring-kawaii-pink max-h-32"
                  />
                  <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    data-testid="send-chat-btn"
                    className="kawaii-btn w-12 h-12 rounded-2xl bg-gradient-to-br from-kawaii-pink to-kawaii-peach text-white flex items-center justify-center shadow-md disabled:opacity-50 flex-shrink-0"
                  >
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" strokeWidth={2.5} />}
                  </button>
                </form>
                <p className="text-[10px] text-kawaii-textSoft mt-2 text-center font-body">
                  Lymphie is AI — not a doctor. Always check in with your care team ♡
                </p>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
